import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Store, Star, TrendingUp, AlertTriangle } from 'lucide-react';
import { apiFetch } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_ICONS = {
  Amazon: '🛒',
  Flipkart: '📦',
  Blinkit: '⚡',
  Zepto: '🚀',
  'Wellversed.in': '🌿',
};

export default function PlatformInsightsPage() {
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlatforms();
  }, []);

  const fetchPlatforms = async () => {
    try {
      const response = await apiFetch('/api/platforms/insights', {
      });
      if (response.ok) {
        setPlatforms(await response.json());
      }
    } catch (error) {
      console.error('Error fetching platforms:', error);
    } finally {
      setLoading(false);
    }
  };

  const chartData = platforms.map((p) => ({
    name: p.platform,
    rating: p.avg_rating,
    reviews: p.total_reviews,
    sentiment: p.sentiment_score,
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
          <p className="font-medium text-sm mb-2">{label}</p>
          {payload.map((entry) => (
            <p key={entry.name} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {entry.value}
              {entry.name === 'Sentiment' ? '%' : ''}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-6 w-48 mb-4" />
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="platforms-page">
      {/* Page header */}
      <div>
        <h1 className="text-3xl font-bold font-heading tracking-tight">Platform Insights</h1>
        <p className="text-muted-foreground mt-1">
          Compare customer feedback across marketplaces
        </p>
      </div>

      {platforms.length > 0 ? (
        <>
          {/* Comparison Chart */}
          <Card className="card-hover" data-testid="platform-comparison-chart">
            <CardHeader>
              <CardTitle className="font-heading">Platform Comparison</CardTitle>
              <CardDescription>Rating and sentiment comparison across platforms</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="name"
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={12}
                    />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar
                      dataKey="rating"
                      name="Avg Rating"
                      fill="#f59e0b"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar
                      dataKey="sentiment"
                      name="Sentiment"
                      fill="#2eb4ac"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Platform Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {platforms.map((platform, idx) => (
              <Card
                key={platform.platform}
                className="card-hover stagger-item"
                data-testid={`platform-card-${idx}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-heading flex items-center gap-2">
                      <span className="text-2xl">{PLATFORM_ICONS[platform.platform] || '🛍️'}</span>
                      {platform.platform}
                    </CardTitle>
                    <Badge
                      className={
                        platform.sentiment_score >= 60
                          ? 'bg-[#2eb4ac]/10 text-[#2eb4ac]'
                          : platform.sentiment_score >= 40
                          ? 'bg-[#f59e0b]/10 text-[#f59e0b]'
                          : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300'
                      }
                    >
                      {platform.sentiment_score}%
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Metrics */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-accent/50 rounded-lg p-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Star className="w-4 h-4 text-[#f59e0b] fill-[#f59e0b]" />
                        <span className="text-xl font-bold">{platform.avg_rating}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Avg Rating</p>
                    </div>
                    <div className="bg-accent/50 rounded-lg p-3 text-center">
                      <span className="text-xl font-bold">{platform.total_reviews}</span>
                      <p className="text-xs text-muted-foreground mt-1">Analysed Reviews</p>
                    </div>
                  </div>

                  {/* Top Issues */}
                  {platform.top_issues?.length > 0 && (
                    <div className="pt-2 border-t border-border">
                      <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
                        <AlertTriangle className="w-3 h-3" />
                        Top Issues on {platform.platform}
                      </div>
                      <div className="space-y-2">
                        {platform.top_issues.slice(0, 3).map((issue) => (
                          <div
                            key={issue.issue}
                            className="flex items-center justify-between text-sm"
                          >
                            <span>{issue.issue}</span>
                            <Badge variant="secondary" className="text-xs">
                              {issue.count}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Store className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Platform Data</h3>
            <p className="text-muted-foreground">
              Upload reviews in Settings to see platform insights.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
